import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FreeVideoPlatforms: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Industry Insights • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          The Economics of Free Video Platforms
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          You upload unlimited videos for free. You watch billions of hours without paying a rupee. Yet platforms like YouTube, IndeeClips, and TikTok operate massive infrastructure, pay creators, and still profit. How do "free" video platforms make money? Why do they exist? And what's the catch? This guide breaks down the business model behind free video platforms—from ad revenue to data monetization.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Core Question: If It's Free, How Do They Make Money?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Short answer: <strong>You're not the customer, you're the product.</strong> Platforms sell access to your attention (ads) and your data (advertising targeting). Here's the breakdown:
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Revenue Stream #1: Advertising (Primary Income)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How It Works
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Brands pay platforms</strong> to show ads to viewers</li>
          <li><strong>Platform inserts ads</strong> into videos (pre-roll, mid-roll, banners)</li>
          <li><strong>Viewers watch ads</strong> (or skip after 5 seconds)</li>
          <li><strong>Platform takes revenue cut</strong> (40-50%), shares rest with creators</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Ad Revenue Math (Example: IndeeClips)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Let's say a video gets 1 million views in India:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>CPM (Cost Per Thousand views):</strong> ₹80-₹150 for Indian traffic (varies by niche)</li>
          <li><strong>Gross ad revenue:</strong> 1M views × ₹100 CPM / 1000 = ₹100,000</li>
          <li><strong>Platform cut (40%):</strong> ₹40,000 to IndeeClips</li>
          <li><strong>Creator cut (60%):</strong> ₹60,000 to creator</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Multiply this across billions of views daily, and platforms make massive ad revenue—YouTube's 2025 ad revenue: $31 billion.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Why Advertisers Pay
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Targeted reach:</strong> Platforms know your age, interests, location—ads reach exact demographics</li>
          <li><strong>High engagement:</strong> Video ads more effective than banner ads on websites</li>
          <li><strong>Performance tracking:</strong> Advertisers see exact clicks, conversions, ROI</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Revenue Stream #2: Premium Subscriptions
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          YouTube Premium / Netflix Model
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Pay a monthly fee (₹129-₹499) for ad-free viewing, offline downloads, and exclusive content.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Why viewers pay:</strong> No interruptions, better mobile experience, exclusive shows</li>
          <li><strong>Platform benefits:</strong> Recurring revenue (predictable cash flow) + still collect watch time data</li>
          <li><strong>Creator benefits:</strong> Premium viewers generate revenue even without ads</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          IndeeClips Premium (Coming 2027)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Planned features: ad-free viewing, 4K streaming, offline downloads, early access to new features. Price: ~₹99/month.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Revenue Stream #3: Data Monetization (The Invisible Revenue)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Data Platforms Collect
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watch history:</strong> What you watch, when, for how long</li>
          <li><strong>Search queries:</strong> Your interests, intent</li>
          <li><strong>Engagement:</strong> Likes, comments, shares</li>
          <li><strong>Device info:</strong> Phone model, OS, location</li>
          <li><strong>Demographics:</strong> Age, gender (inferred from behavior)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How Data Is Monetized
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Ad targeting:</strong> The more platforms know about you, the more advertisers pay (targeted ads = higher CPMs)</li>
          <li><strong>Recommendation engines:</strong> Data trains AI to keep you watching longer = more ad impressions</li>
          <li><strong>Trend analysis:</strong> Platforms sell aggregated insights to brands (e.g., "18-25 year olds in Mumbai love tech content")</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Note: IndeeClips does NOT sell individual user data to third parties. We use data internally for recommendations and ad targeting only.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Revenue Stream #4: Creator Partnerships and Sponsorships
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Platform-Facilitated Brand Deals
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Platforms connect brands with creators, take a commission (10-20%) on deals.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Example: Brand wants to sponsor 10 tech creators for a product launch</li>
          <li>YouTube/IndeeClips facilitates intros, manages contracts</li>
          <li>Platform takes ₹50K cut on a ₹500K deal</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Affiliate Marketing Revenue Sharing
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Some platforms take a small cut when creators use affiliate links. YouTube's merch shelf takes 15% of sales.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Cost Side: What Platforms Spend Money On
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Infrastructure Costs (Largest Expense)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Storage:</strong> Petabytes of video = millions per month</li>
          <li><strong>Bandwidth:</strong> Streaming billions of hours = massive data transfer costs</li>
          <li><strong>CDN (Content Delivery Network):</strong> Edge servers worldwide cost millions</li>
          <li><strong>Transcoding:</strong> Converting uploaded videos to multiple qualities</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          YouTube spends ~$4 billion annually on infrastructure. IndeeClips (smaller scale) spends ~₹50 crore/year.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Creator Payments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Platforms share ad revenue with creators (55-60% typically). YouTube paid creators $30 billion in 2023-2025.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Content Moderation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Thousands of human moderators + AI systems to filter illegal/harmful content. YouTube's moderation budget: ~$500 million/year.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Technology and Engineering
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Salaries for engineers, product managers, designers. Top platforms have teams of 5,000+ engineers.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Profitability: Are Free Platforms Actually Profitable?
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          YouTube (Profitable)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Revenue:</strong> $31 billion (2025)</li>
          <li><strong>Costs:</strong> ~$25 billion (infrastructure, creator payments, moderation)</li>
          <li><strong>Profit:</strong> ~$6 billion</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          TikTok (Break-Even, Prioritizing Growth)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Massive infrastructure costs + creator fund payments</li>
          <li>Investing heavily in growth over profit (standard startup strategy)</li>
          <li>Will turn profitable once user growth stabilizes</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          IndeeClips (Pre-Profitable, Growth Phase)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Current status:</strong> Not yet profitable (burning cash to grow user base)</li>
          <li><strong>Strategy:</strong> Reach 50M users, then turn on full monetization (ads, premium)</li>
          <li><strong>Target profitability:</strong> 2028</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Why Venture Capital Funds "Free" Platforms
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Many free platforms (IndeeClips, early YouTube, TikTok) lose money for years. Why do investors fund them?
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Network effects:</strong> More users = more creators = more content = more users (compounding growth)</li>
          <li><strong>Winner-takes-most market:</strong> The largest platform dominates (YouTube {'>'} Vimeo)</li>
          <li><strong>Long-term profit potential:</strong> Once user base is large, flip on monetization = instant revenue</li>
          <li><strong>Exit strategy:</strong> Investors aim to sell (IPO or acquisition) before profitability</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Trade-Offs: What You Give Up for "Free"
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Privacy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your watch history, searches, and behavior are tracked. This data trains algorithms and targets ads.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Ads Everywhere
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Interruptions every few minutes. Ad-free experiences require premium subscriptions.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Algorithmic Control
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Platforms decide what you see. Recommendations optimize for watch time, not what's best for you.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Alternative Model: Paid Platforms (Netflix, Hotstar)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Instead of ads, charge subscriptions. Pros and cons:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Pros:</strong> No ads, better privacy, content curated for quality</li>
          <li><strong>Cons:</strong> Barrier to entry (₹199-₹499/month), smaller user base</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Paid platforms work for premium content (shows, movies) but struggle for user-generated content (YouTube, IndeeClips).
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            "Free" Is a Business Model, Not a Gift
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Free platforms aren't charity—they're ad-funded businesses optimizing for scale. Understanding the economics helps you make informed choices: tolerate ads for free access, pay for premium, or limit usage to protect privacy. As a creator, knowing how platforms make money helps you align incentives—produce high-retention content, understand CPMs, and diversify income beyond platform ads.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about IndeeClips' business model? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FreeVideoPlatforms;
